import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ClientSuccessSection = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const clientStories = [
  {
    id: 'acMilan',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_140a98699-1769696764660.png",
    imageAlt: 'Professional football director in formal attire at AC Milan San Siro stadium',
    playersAcquired: 7
  },
  {
    id: 'fifa',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ac849fd9-1769696766086.png",
    imageAlt: 'Senior FIFA official in business suit at international football conference',
    playersAcquired: 0
  },
  {
    id: 'orlandoPirates',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ade4dc84-1770917636510.png",
    imageAlt: 'African football director in Orlando Pirates team colors at stadium',
    playersAcquired: 12
  },
  {
    id: 'sundowns',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15fbe70e9-1768330890196.png",
    imageAlt: 'South African football scout with tactical board at Sundowns training facility',
    playersAcquired: 15
  },
  {
    id: 'mcAlger',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b9e97cd8-1763295072915.png",
    imageAlt: 'Algerian football director in professional attire at MC Alger headquarters',
    playersAcquired: 9
  },
  {
    id: 'usmAlger',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15fbe70e9-1768330890196.png",
    imageAlt: 'Senior scout from USM Alger reviewing player performance data',
    playersAcquired: 11
  }];


  const stats = [
  { label: t('clientSuccess.eliteClubs'), value: '25+', icon: 'Building', gradient: 'from-blue-600 to-blue-700' },
  { label: t('clientSuccess.playersPlaced'), value: '180+', icon: 'Users', gradient: 'from-emerald-600 to-emerald-700' },
  { label: t('clientSuccess.totalTransferValue'), value: '€250M+', icon: 'TrendingUp', gradient: 'from-purple-600 to-purple-700' },
  { label: t('clientSuccess.successRate'), value: '96%', icon: 'Award', gradient: 'from-amber-600 to-amber-700' }];


  return (
    <section className="py-16 md:py-24 lg:py-32 bg-gradient-to-b from-white to-slate-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(0,0,0,0.15) 1px, transparent 0)',
        backgroundSize: '32px 32px'
      }} />

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full mb-6 shadow-sm">
            <Icon name="Star" size={18} className="text-blue-600" />
            <span className="text-sm font-bold text-slate-800">{t('clientSuccess.clientSuccessStories')}</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-6xl font-black text-slate-900 mb-6 leading-tight">
            <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">
              {t('clientSuccess.trustedByElite')}
            </span>
          </h2>
          <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            {t('clientSuccess.leadingClubsRely')}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16 md:mb-20">
          {stats?.map((stat, index) =>
          <div
            key={index}
            className="group relative bg-white border-2 border-slate-200 rounded-2xl p-6 md:p-8 text-center hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden">
              {/* Gradient Background on Hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${stat?.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`} />
              
              {/* Icon */}
              <div className={`relative w-14 h-14 md:w-16 md:h-16 rounded-xl bg-gradient-to-br ${stat?.gradient} flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:scale-110 transition-transform duration-500`}>
                <Icon name={stat?.icon} size={28} color="white" />
              </div>
              
              {/* Value */}
              <div className="relative text-3xl md:text-4xl font-black text-slate-900 mb-2">{stat?.value}</div>
              
              {/* Label */}
              <div className="relative text-sm md:text-base text-slate-600 font-semibold">{stat?.label}</div>
            </div>
          )}
        </div>

        {/* Client Stories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-16">
          {clientStories?.map((story, index) =>
          <div
            key={index}
            className="group bg-white border-2 border-slate-200 rounded-3xl overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
              {/* Client Image */}
              <div className="relative h-56 overflow-hidden">
                <Image
                src={story?.image}
                alt={story?.imageAlt}
                className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700" />
                
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent" />
                
                {/* Club Info */}
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-2xl font-black text-white mb-1">{t(`clientSuccess.stories.${story?.id}.club`)}</h3>
                  <p className="text-sm text-white/90 font-semibold">{t(`clientSuccess.stories.${story?.id}.league`)} • {t(`clientSuccess.stories.${story?.id}.country`)}</p>
                </div>
              </div>

              {/* Card Content */}
              <div className="p-6">
                {/* Players Acquired Badge */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center">
                      <Icon name="Users" size={20} color="white" />
                    </div>
                    <div>
                      <div className="text-2xl font-black text-blue-900">{story?.playersAcquired}</div>
                      <div className="text-xs text-blue-700 font-bold">{t('clientSuccess.playersAcquired')}</div>
                    </div>
                  </div>
                </div>

                {/* Testimonial */}
                <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-5 mb-4">
                  <Icon name="Quote" size={18} className="text-slate-400 mb-3" />
                  <p className="text-sm text-slate-700 italic leading-relaxed mb-4">{t(`clientSuccess.stories.${story?.id}.testimonial`)}</p>
                  
                  {/* Contact Info */}
                  <div className="flex items-center gap-3 pt-4 border-t-2 border-slate-200">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center">
                      <Icon name="User" size={18} color="white" />
                    </div>
                    <div>
                      <div className="font-black text-sm text-slate-900">{t(`clientSuccess.stories.${story?.id}.contact`)}</div>
                      <div className="text-xs text-slate-600 font-semibold">{t(`clientSuccess.stories.${story?.id}.role`)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* CTA Banner */}
        <div className="relative bg-gradient-to-br from-blue-600 via-purple-600 to-blue-700 rounded-3xl p-8 md:p-12 lg:p-16 text-center overflow-hidden shadow-2xl">
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
          
          {/* Content */}
          <div className="relative z-10">
            <h3 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-6 leading-tight">
              {t('clientSuccess.joinLeadingClubs')}
            </h3>
            <p className="text-lg md:text-xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed">
              {t('clientSuccess.partnerWithSportProX')}
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                iconName="Handshake"
                iconPosition="left"
                onClick={() => navigate('/contact')}
                className="bg-white text-blue-600 hover:bg-blue-50 shadow-xl border-0 text-base font-bold px-10 py-6">
                {t('clientSuccess.becomePartner')}
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="BookOpen"
                iconPosition="left"
                onClick={() => navigate('/success-stories')}
                className="border-2 border-white text-white hover:bg-white/10 backdrop-blur-sm text-base font-bold px-10 py-6">
                {t('clientSuccess.readMoreStories')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default ClientSuccessSection;